import { createRouter, type RouterHistory } from "@tanstack/react-router";
import { NotFoundBoundary, routeTree } from "./route-tree";
import { RouteErrorBoundary } from "./route-error";

/**
 * Plain-string search serialization. Every value the client reads from the
 * query string it coerces to a string (nothing validates a typed search
 * schema), so the router's default serializer only ever caused harm: it
 * JSON-encodes any value that parses as JSON, writing page 2 as `page=%222%22`
 * and a search for `2024` as `q=%222024%22`. Rebuilt from the address bar by
 * useRouteNavigate, those re-encoded again, so Back from a repository came back
 * with NaN for the page and quotes in the search box. Keeping values untouched
 * both ways leaves the round trip clean.
 */
export const searchSerialization = {
  parseSearch: (searchStr: string): Record<string, string> => {
    const query = searchStr.startsWith("?") ? searchStr.slice(1) : searchStr;
    return Object.fromEntries(new URLSearchParams(query));
  },
  stringifySearch: (search: Record<string, unknown>): string => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value === undefined || value === null) continue;
      params.set(key, String(value));
    }
    const query = params.toString();
    return query ? `?${query}` : "";
  },
};

export function buildRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    ...searchSerialization,
    // On the router rather than the root route: a path that matches nothing at
    // all never reaches a route's own boundary.
    defaultNotFoundComponent: NotFoundBoundary,
    defaultErrorComponent: RouteErrorBoundary,
    // The shell reads the pathname to decide what to render, so a route that
    // resolves to nothing must still reach the shell rather than blanking.
    defaultPreload: false,
    // Suspense boundaries already sit inside each route component; a second
    // pending delay here would show a flash of nothing on every navigation.
    defaultPendingMs: 0,
  });
}

export const router = buildRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
